import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockSubjects, mockUsers } from '../../data/mockData';
import { dateUtils } from '../../utils';

const SubjectAssignment = ({ project, onUpdate, saving }) => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [editingSubject, setEditingSubject] = useState(null);
  const [addingClassForSubject, setAddingClassForSubject] = useState(null);
  const [editingClass, setEditingClass] = useState(null);

  // Opciones disponibles para objetivos y habilidades
  const availableObjectives = [
    'Comprender conceptos fundamentales',
    'Aplicar conocimientos en contextos reales',
    'Desarrollar pensamiento crítico',
    'Fomentar la creatividad',
    'Promover el trabajo en equipo',
    'Resolver problemas complejos',
    'Comunicar ideas efectivamente',
    'Investigar y analizar información'
  ];

  const availableSkills = [
    'Análisis crítico',
    'Resolución de problemas',
    'Trabajo colaborativo',
    'Comunicación efectiva',
    'Pensamiento creativo',
    'Investigación',
    'Liderazgo',
    'Autogestión'
  ];

  useEffect(() => {
    // Inicializar datos con mock de clases
    const subjectsWithClasses = (project.subjects || []).map(subject => ({
      ...subject,
      objectives: subject.objectives || ['Comprender conceptos fundamentales', 'Aplicar conocimientos en contextos reales'],
      skills: subject.skills || ['Análisis crítico', 'Resolución de problemas', 'Trabajo colaborativo'],
      classes: subject.classes || [
        {
          id: 1,
          date: '2024-10-15',
          detail: 'Introducción al tema: presentación de conceptos básicos y marco teórico',
          materials: ['Presentación PPT', 'Guía de estudio', 'Videos explicativos']
        },
        {
          id: 2,
          date: '2024-10-22',
          detail: 'Desarrollo práctico: ejercicios y casos de aplicación',
          materials: ['Manual de ejercicios', 'Casos de estudio']
        },
        {
          id: 3,
          date: '2024-10-29',
          detail: 'Evaluación formativa y retroalimentación',
          materials: ['Rúbrica de evaluación', 'Formulario de autoevaluación']
        }
      ]
    }));
    setSubjects(subjectsWithClasses);
    setAvailableSubjects(mockSubjects);
    setAvailableTeachers(mockUsers.filter(user => user.role === 'profesor'));
  }, [project]);

  const handleAddSubject = () => {
    setShowAddSubject(true);
  };

  const handleAssignSubject = (subjectData) => {
    const newSubject = {
      id: Date.now(),
      subjectId: subjectData.subjectId,
      name: availableSubjects.find(s => s.id === subjectData.subjectId)?.name,
      teacherId: subjectData.teacherId,
      teacherName: availableTeachers.find(t => t.id === subjectData.teacherId)?.name,
      objectives: ['Comprender conceptos fundamentales', 'Aplicar conocimientos en contextos reales'],
      skills: ['Análisis crítico', 'Resolución de problemas'],
      classes: [],
      status: 'asignada',
      createdAt: new Date().toISOString()
    };

    const updatedSubjects = [...subjects, newSubject];
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
    setShowAddSubject(false);
  };

  const handleRemoveSubject = (subjectId) => {
    const updatedSubjects = subjects.filter(s => s.id !== subjectId);
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
    if (selectedSubject?.id === subjectId) {
      setSelectedSubject(null);
    }
  };

  const handleEditSubject = (subject) => {
    setEditingSubject(subject);
  };

  const handleUpdateSubject = (updatedData) => {
    const updatedSubjects = subjects.map(s =>
      s.id === updatedData.id
        ? {
            ...s,
            teacherId: updatedData.teacherId,
            teacherName: availableTeachers.find(t => t.id === updatedData.teacherId)?.name,
            objectives: updatedData.objectives,
            skills: updatedData.skills
          }
        : s
    );
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
    setEditingSubject(null);
  };

  const handleAddClass = (subjectId) => {
    setAddingClassForSubject(subjectId);
  };

  const handleSaveNewClass = (subjectId, classData) => {
    const updatedSubjects = subjects.map(s => {
      if (s.id === subjectId) {
        const newClass = {
          id: Date.now(),
          ...classData
        };
        return {
          ...s,
          classes: [...(s.classes || []), newClass]
        };
      }
      return s;
    });
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
    setAddingClassForSubject(null);
  };

  const handleEditClass = (subjectId, classItem) => {
    setEditingClass({ subjectId, classItem });
  };

  const handleUpdateClass = (subjectId, updatedClassData) => {
    const updatedSubjects = subjects.map(s => {
      if (s.id === subjectId) {
        return {
          ...s,
          classes: s.classes.map(c =>
            c.id === updatedClassData.id ? updatedClassData : c
          )
        };
      }
      return s;
    });
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
    setEditingClass(null);
  };

  const handleDeleteClass = (subjectId, classId) => {
    const updatedSubjects = subjects.map(s => {
      if (s.id === subjectId) {
        return {
          ...s,
          classes: s.classes.filter(c => c.id !== classId)
        };
      }
      return s;
    });
    setSubjects(updatedSubjects);
    onUpdate({ subjects: updatedSubjects });
  };

  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
  };

  const getLatestClass = (subject) => {
    if (!subject.classes || subject.classes.length === 0) return null;
    // Ordenar por fecha descendente y retornar la más reciente
    const sorted = [...subject.classes].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0];
  };

  // Distribuir asignaturas formando una O
  const distributeSubjects = () => {
    const totalSubjects = subjects.length;
    if (totalSubjects === 0) return { top: [], left: [], right: [], bottom: [] };

    // Calcular distribución para formar una O
    const topCount = Math.min(3, Math.ceil(totalSubjects / 2));
    const bottomCount = Math.max(0, totalSubjects - topCount - 2);
    const leftCount = totalSubjects > topCount ? 1 : 0;
    const rightCount = totalSubjects > topCount + leftCount ? 1 : 0;

    let index = 0;
    const top = subjects.slice(index, index + topCount);
    index += topCount;
    const left = subjects.slice(index, index + leftCount);
    index += leftCount;
    const right = subjects.slice(index, index + rightCount);
    index += rightCount;
    const bottom = subjects.slice(index);

    return { top, left, right, bottom };
  };

  const distribution = distributeSubjects();

  return (
    <div className="relative space-y-6">
      {/* Fila superior - Asignaturas */}
      {distribution.top.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {distribution.top.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              latestClass={getLatestClass(subject)}
              onSubjectClick={handleSubjectClick}
              onAddClass={handleAddClass}
              onEditSubject={handleEditSubject}
              onRemoveSubject={handleRemoveSubject}
              projectId={project.id}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      {/* Fila del medio - Laterales + Centro */}
      {(distribution.left.length > 0 || distribution.right.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna izquierda */}
          <div className="lg:col-span-1">
            {distribution.left.map((subject) => (
              <SubjectCard
                key={subject.id}
                subject={subject}
                latestClass={getLatestClass(subject)}
                onSubjectClick={handleSubjectClick}
                onAddClass={handleAddClass}
                onEditSubject={handleEditSubject}
                onRemoveSubject={handleRemoveSubject}
                projectId={project.id}
                navigate={navigate}
              />
            ))}
          </div>

          {/* Centro - Información del Proyecto */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-primary-50 to-blue-50 border-2 border-primary-200 rounded-2xl p-6 sticky top-6 shadow-lg">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Información del Proyecto</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Objetivo General
                  </h4>
                  <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
                    {project.generalObjective || 'No se ha definido un objetivo general para este proyecto'}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Producto Esperado
                  </h4>
                  <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
                    {project.expectedProduct || 'No se ha definido un producto esperado para este proyecto'}
                  </p>
                </div>

                {project.thinkingSkill && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Habilidad de Pensamiento
                    </h4>
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-primary-100 text-primary-800 border border-primary-300">
                        {project.thinkingSkill}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleAddSubject}
                  disabled={saving}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 shadow-md"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Agregar Asignatura</span>
                </button>
              </div>
            </div>
          </div>

          {/* Columna derecha */}
          <div className="lg:col-span-1">
            {distribution.right.map((subject) => (
              <SubjectCard
                key={subject.id}
                subject={subject}
                latestClass={getLatestClass(subject)}
                onSubjectClick={handleSubjectClick}
                onAddClass={handleAddClass}
                onEditSubject={handleEditSubject}
                onRemoveSubject={handleRemoveSubject}
                projectId={project.id}
                navigate={navigate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Si no hay laterales, mostrar solo el centro */}
      {distribution.left.length === 0 && distribution.right.length === 0 && subjects.length > 0 && (
        <div className="max-w-xl mx-auto">
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 border-2 border-primary-200 rounded-2xl p-6 shadow-lg">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Información del Proyecto</h3>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Objetivo General
                </h4>
                <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200">
                  {project.generalObjective || 'No se ha definido un objetivo general'}
                </p>
              </div>
              <button onClick={handleAddSubject} disabled={saving} className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 rounded-lg font-medium flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Agregar Asignatura</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fila inferior - Asignaturas */}
      {distribution.bottom.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {distribution.bottom.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              latestClass={getLatestClass(subject)}
              onSubjectClick={handleSubjectClick}
              onAddClass={handleAddClass}
              onEditSubject={handleEditSubject}
              onRemoveSubject={handleRemoveSubject}
              projectId={project.id}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      {/* Estado vacío */}
      {subjects.length === 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h4 className="text-xl font-medium text-gray-900 mb-2">No hay asignaturas asignadas</h4>
          <p className="text-gray-600 mb-6">Comienza asignando la primera asignatura al proyecto</p>
          <button
            onClick={handleAddSubject}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Asignar Primera Asignatura</span>
          </button>
        </div>
      )}

      {/* Sidebar con detalle de clases */}
      {selectedSubject && (
        <ClassDetailSidebar
          subject={selectedSubject}
          onClose={() => setSelectedSubject(null)}
          onEditClass={handleEditClass}
          onDeleteClass={handleDeleteClass}
        />
      )}

      {/* Modal para Asignar Asignatura */}
      {showAddSubject && (
        <AssignSubjectModal
          availableSubjects={availableSubjects}
          availableTeachers={availableTeachers}
          assignedSubjects={subjects}
          onAssign={handleAssignSubject}
          onCancel={() => setShowAddSubject(false)}
        />
      )}

      {/* Modal para Editar Asignatura */}
      {editingSubject && (
        <EditSubjectModal
          subject={editingSubject}
          availableTeachers={availableTeachers}
          availableObjectives={availableObjectives}
          availableSkills={availableSkills}
          onUpdate={handleUpdateSubject}
          onCancel={() => setEditingSubject(null)}
        />
      )}

      {/* Modal para Agregar Clase */}
      {addingClassForSubject && (
        <AddClassModal
          subjectId={addingClassForSubject}
          onSave={handleSaveNewClass}
          onCancel={() => setAddingClassForSubject(null)}
        />
      )}

      {/* Modal para Editar Clase */}
      {editingClass && (
        <EditClassModal
          subjectId={editingClass.subjectId}
          classItem={editingClass.classItem}
          onUpdate={handleUpdateClass}
          onCancel={() => setEditingClass(null)}
        />
      )}
    </div>
  );
};

// Componente de Tarjeta de Asignatura
const SubjectCard = ({ subject, latestClass, onSubjectClick, onAddClass, onEditSubject, onRemoveSubject, projectId, navigate }) => {
  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all hover:border-primary-300">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <button
          onClick={() => onSubjectClick(subject)}
          className="flex-1 text-left group"
        >
          <h4 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
            {subject.name}
          </h4>
          <div className="flex items-center text-gray-600 mt-1">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="text-sm">{subject.teacherName || 'Sin profesor'}</span>
          </div>
        </button>
        <div className="flex items-center space-x-1 ml-2">
          <button
            onClick={() => navigate(`/projects/${projectId}/subjects/${subject.id}/evaluation`)}
            className="text-blue-600 hover:text-blue-800 p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
            title="Configurar evaluación"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <button
            onClick={() => onAddClass(subject.id)}
            className="text-green-600 hover:text-green-800 p-1.5 rounded-lg hover:bg-green-50 transition-colors"
            title="Agregar clase"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button
            onClick={() => onEditSubject(subject)}
            className="text-primary-600 hover:text-primary-800 p-1.5 rounded-lg hover:bg-primary-50 transition-colors"
            title="Editar asignatura"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onRemoveSubject(subject.id)}
            className="text-red-600 hover:text-red-800 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
            title="Remover asignatura"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Objetivos de Aprendizaje */}
      {subject.objectives && subject.objectives.length > 0 && (
        <div className="mb-3">
          <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Objetivos de Aprendizaje
          </h5>
          <ul className="space-y-1">
            {subject.objectives.slice(0, 2).map((objective, idx) => (
              <li key={idx} className="flex items-start text-xs text-gray-600">
                <span className="inline-block w-1 h-1 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                <span className="line-clamp-1">{objective}</span>
              </li>
            ))}
            {subject.objectives.length > 2 && (
              <li className="text-xs text-gray-500 ml-3">+{subject.objectives.length - 2} más</li>
            )}
          </ul>
        </div>
      )}

      {/* Habilidades */}
      {subject.skills && subject.skills.length > 0 && (
        <div className="mb-3">
          <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
            <svg className="w-3 h-3 mr-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Habilidades
          </h5>
          <div className="flex flex-wrap gap-1">
            {subject.skills.slice(0, 3).map((skill, idx) => (
              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                {skill}
              </span>
            ))}
            {subject.skills.length > 3 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                +{subject.skills.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Última clase */}
      {latestClass ? (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="flex items-center text-xs text-gray-500 mb-2">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Última clase: {dateUtils.formatDate(latestClass.date)}</span>
          </div>
          <p className="text-sm text-gray-700 mb-2 line-clamp-2">{latestClass.detail}</p>
          {latestClass.materials && latestClass.materials.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {latestClass.materials.slice(0, 2).map((material, idx) => (
                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-200">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {material}
                </span>
              ))}
              {latestClass.materials.length > 2 && (
                <span className="text-xs text-gray-500">+{latestClass.materials.length - 2}</span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
          <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-xs text-gray-500">Sin clases registradas</p>
        </div>
      )}

      {/* Total de clases */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>Total: {subject.classes?.length || 0} clases</span>
        <button
          onClick={() => onSubjectClick(subject)}
          className="text-primary-600 hover:text-primary-700 font-medium flex items-center"
        >
          Ver todas
          <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Componente Sidebar con detalle de clases
const ClassDetailSidebar = ({ subject, onClose, onEditClass, onDeleteClass }) => {
  // Ordenar clases de más reciente a más antigua
  const sortedClasses = [...(subject.classes || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      ></div>

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 xl:w-1/3 bg-white shadow-2xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{subject.name}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {subject.teacherName || 'Sin profesor asignado'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Clases ({sortedClasses.length})
          </h4>

          {sortedClasses.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500">No hay clases registradas</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedClasses.map((classItem, idx) => (
                <div key={classItem.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-700">
                        Clase {sortedClasses.length - idx}
                      </span>
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {dateUtils.formatDate(classItem.date)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onEditClass(subject.id, classItem)}
                        className="text-primary-600 hover:text-primary-800 p-1 rounded hover:bg-primary-50 transition-colors"
                        title="Editar clase"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteClass(subject.id, classItem.id)}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition-colors"
                        title="Eliminar clase"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h6 className="text-xs font-semibold text-gray-500 uppercase mb-1">Detalle</h6>
                    <p className="text-sm text-gray-700">{classItem.detail}</p>
                  </div>

                  {classItem.materials && classItem.materials.length > 0 && (
                    <div>
                      <h6 className="text-xs font-semibold text-gray-500 uppercase mb-2">Materiales</h6>
                      <div className="flex flex-wrap gap-2">
                        {classItem.materials.map((material, mIdx) => (
                          <span key={mIdx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white text-gray-700 border border-gray-300">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            {material}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Modal para Asignar Asignatura - Versión simplificada
const AssignSubjectModal = ({ availableSubjects, availableTeachers, assignedSubjects, onAssign, onCancel }) => {
  const [formData, setFormData] = React.useState({ subjectId: '', teacherId: '' });
  const [errors, setErrors] = React.useState({});
  const assignedSubjectIds = assignedSubjects.map(s => s.subjectId);
  const unassignedSubjects = availableSubjects.filter(s => !assignedSubjectIds.includes(s.id));

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.subjectId) newErrors.subjectId = 'Requerido';
    if (!formData.teacherId) newErrors.teacherId = 'Requerido';
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    onAssign(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Asignar Asignatura</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Asignatura *</label>
            <select value={formData.subjectId} onChange={(e) => setFormData({...formData, subjectId: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Seleccionar...</option>
              {unassignedSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-2">Profesor *</label>
            <select value={formData.teacherId} onChange={(e) => setFormData({...formData, teacherId: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Seleccionar...</option>
              {availableTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={onCancel} className="flex-1 bg-gray-500 text-white py-2 rounded-lg">Cancelar</button>
            <button type="submit" className="flex-1 bg-primary-500 text-white py-2 rounded-lg">Asignar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditSubjectModal = ({ subject, availableTeachers, availableObjectives, availableSkills, onUpdate, onCancel }) => {
  const [formData, setFormData] = React.useState({ id: subject.id, teacherId: subject.teacherId || '', objectives: subject.objectives || [], skills: subject.skills || [] });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Editar: {subject.name}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Profesor *</label>
            <select value={formData.teacherId} onChange={(e) => setFormData({...formData, teacherId: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Seleccionar...</option>
              {availableTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={onCancel} className="flex-1 bg-gray-500 text-white py-2 rounded-lg">Cancelar</button>
            <button type="submit" className="flex-1 bg-primary-500 text-white py-2 rounded-lg">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddClassModal = ({ subjectId, onSave, onCancel }) => {
  const [formData, setFormData] = React.useState({ date: '', detail: '', materials: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const materialsArray = formData.materials.split(',').map(m => m.trim()).filter(m => m.length > 0);
    onSave(subjectId, { ...formData, materials: materialsArray });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Agregar Clase</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Fecha *</label>
            <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm mb-2">Descripción *</label>
            <textarea value={formData.detail} onChange={(e) => setFormData({...formData, detail: e.target.value})} rows={4} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm mb-2">Materiales</label>
            <input type="text" value={formData.materials} onChange={(e) => setFormData({...formData, materials: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Separados por comas" />
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={onCancel} className="flex-1 bg-gray-500 text-white py-2 rounded-lg">Cancelar</button>
            <button type="submit" className="flex-1 bg-primary-500 text-white py-2 rounded-lg">Agregar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditClassModal = ({ subjectId, classItem, onUpdate, onCancel }) => {
  const [formData, setFormData] = React.useState({ date: classItem.date || '', detail: classItem.detail || '', materials: classItem.materials?.join(', ') || '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const materialsArray = formData.materials.split(',').map(m => m.trim()).filter(m => m.length > 0);
    onUpdate(subjectId, { id: classItem.id, ...formData, materials: materialsArray });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Editar Clase</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Fecha *</label>
            <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm mb-2">Descripción *</label>
            <textarea value={formData.detail} onChange={(e) => setFormData({...formData, detail: e.target.value})} rows={4} className="w-full px-3 py-2 border rounded-lg" required />
          </div>
          <div>
            <label className="block text-sm mb-2">Materiales</label>
            <input type="text" value={formData.materials} onChange={(e) => setFormData({...formData, materials: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Separados por comas" />
          </div>
          <div className="flex space-x-3">
            <button type="button" onClick={onCancel} className="flex-1 bg-gray-500 text-white py-2 rounded-lg">Cancelar</button>
            <button type="submit" className="flex-1 bg-primary-500 text-white py-2 rounded-lg">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubjectAssignment;
